import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Reusable page header used across Wheere apps.
 *
 * Renders the integrator-style page chrome: a Syne-font colored title with
 * an optional subtitle and an action slot on the right. Tab-color accents
 * mirror the integrator's section palette so each page can declare which
 * brand color it belongs to.
 *
 * ```html
 * <lib-page-header
 *   accent="electricPurple"
 *   [title]="'siteList.title' | transloco"
 *   [subtitle]="'siteList.subtitle' | transloco: { sites: total() }">
 *   <button libButton>{{ 'siteList.new' | transloco }}</button>
 * </lib-page-header>
 * ```
 *
 * Content projected via the default slot lands in the right-aligned actions
 * area. The whole header collapses gracefully on mobile (title shrinks,
 * actions wrap below the text).
 */
export type PageHeaderAccent =
  | 'primary' // brand pink (devices, dashboard)
  | 'electricPurple' // sites
  | 'warmYellow' // gateways
  | 'aquamarine' // team / audit / users
  | 'neutral'; // catch-all

@Component({
  standalone: true,
  selector: 'lib-page-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
      <div class="min-w-0 flex-1">
        <h1 class="wh-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl" [class]="titleColorClass()">
          {{ title() }}
        </h1>
        @if (subtitle(); as s) {
          <p class="mt-1 text-sm text-gray-700 dark:text-slate-300">{{ s }}</p>
        }
      </div>
      <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
        <ng-content />
      </div>
    </header>
  `,
})
export class PageHeaderComponent {
  /** Rendered as the main title. Pre-translate before passing in. */
  readonly title = input.required<string>();

  /** Optional one-line subtitle (counts, breadcrumbs, last-updated). */
  readonly subtitle = input<string | null | undefined>(null);

  /** Tailwind color hook. Defaults to neutral so callers that don't set it
   * still render a sensible header. */
  readonly accent = input<PageHeaderAccent>('neutral');

  /** Compute the title color class from the accent. Tailwind purges classes
   * by string match, so each branch returns a literal class name (no
   * dynamic concatenation). */
  protected readonly titleColorClass = computed<string>(() => {
    switch (this.accent()) {
      case 'primary':
        return 'text-primary-500';
      case 'electricPurple':
        return 'text-eletricPurple-500';
      case 'warmYellow':
        return 'text-warmYellow-500';
      case 'aquamarine':
        return 'text-aquamarine-500';
      case 'neutral':
      default:
        return 'text-gray-900 dark:text-slate-100';
    }
  });
}
